import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api.js';
import logo from '../src/assests/wealth_plus.png';

const Login = ({ onLogin, onSwitchToRegister, onSwitchToForgotPassword }) => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [identifierError, setIdentifierError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const passwordRef = useRef(null);

  const navigate = useNavigate();
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setIdentifierError('');
    setPasswordError('');

    // Basic client-side validation — email only
    if (!identifier || identifier.trim().length === 0) {
      setIdentifierError('Please enter your email');
      setLoading(false);
      return;
    }
    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!emailRegex.test(identifier)) {
      setIdentifierError('Please enter a valid email address');
      setLoading(false);
      return;
    }
    if (!password || password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }
    try {
      const result = await authAPI.login({ identifier, password });


      // The API returns { accessToken, refreshToken, user }
      if (result && result.user) {
        onLogin(result.user);
        navigate('/dashboard');
      } else {
        setError('Login failed - invalid response from server');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 py-12 px-4 sm:px-6 lg:px-8 rounded-xl">
      <div className="max-w-md w-full">
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg ring-1 ring-gray-100">
          <div className="flex justify-center">
            <img src={logo} alt="Wealth Plus Logo" className="h-15 w-20" />
          </div>
          <h2 className=" text-center text-2xl font-bold text-gray-900">
            WELCOME
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to your Wealth Plus account
          </p>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md -space-y-px">
              <div className="mb-3">
                <label htmlFor="identifier" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  id="identifier"
                  name="identifier"
                  type="email"
                  autoFocus
                  aria-invalid={identifierError ? 'true' : 'false'}
                  required
                  className={`mt-1 block w-full px-3 py-2 border ${identifierError ? 'border-red-400' : 'border-gray-300'} rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                  placeholder="you@example.com"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                />
                {identifierError && <p className="mt-1 text-sm text-red-600">{identifierError}</p>}
              </div>

              <div className="mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="relative mt-1">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    ref={passwordRef}
                    autoComplete="current-password"
                    aria-invalid={passwordError ? 'true' : 'false'}
                    required
                    className={`block w-full pl-3 pr-10 py-2 border ${passwordError ? 'border-red-400' : 'border-gray-300'} rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                {passwordError && <p className="mt-1 text-sm text-red-600">{passwordError}</p>}
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                {/* <div className="flex items-center">
                  <input
                    id="remember_me"
                    name="remember_me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember_me" className="ml-2 block text-sm text-gray-700">
                    Remember me
                  </label>
                </div> */}

                <div className="text-sm">
                  <button
                    type="button"
                    onClick={onSwitchToForgotPassword}
                    className="font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    Forgot your password?
                  </button>
                </div>
              </div>

              <div className="mt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>

              {/* Sign-up removed: accounts are created by administrators only */}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;